import urql, { Client, fetchExchange, mapExchange } from '@urql/vue'
import {
  filter,
  map,
  merge,
  pipe
} from 'wonka'
import { cacheExchange } from '@urql/exchange-graphcache'
import { authExchange as AuthExchange } from '@urql/exchange-auth'
import ErrorHandler from '~/gql-errors/error-handler'
import hasValidAccessToken from '~/middleware/auth-utils/hasValidAccessToken'
import silentlyAuthenticateWithAuth0 from '~/middleware/auth-utils/silentlyAuthenticateWithAuth0'

function isAuthenticationError(errorContext) {
  const isUnauthenticated = errorContext?.graphQLErrors?.some(gqlError => gqlError.extensions.code === 'UNAUTHENTICATED')

  return Boolean(isUnauthenticated || errorContext.response?.status === 401)
}

// Use Auth0 to retrieve the updated token silently and update the authentication cookie with the new token.
async function getAuthToken(authTokenName) {
  const { $auth, $bugsnag } = useNuxtApp()

  const silentAccessToken = await silentlyAuthenticateWithAuth0($auth, $bugsnag)

  if (!silentAccessToken) {
    // Send user to Auth0 login page
    $auth.authorize({})
    return
  }

  // Update the auth token cookie.
  const authCookie = useCookie(authTokenName)

  authCookie.value = silentAccessToken

  return silentAccessToken
}

function getAuthExchange(authTokenName) {
  return AuthExchange((utils) => {
    return {
      addAuthToOperation(operation) {
        const authCookie = useCookie(authTokenName)

        if (!authCookie.value) {
          return operation
        }

        return utils.appendHeaders(operation, {
          Authorization: `Bearer ${authCookie.value}`,
        })
      },
      willAuthError() {
        const authCookie = useCookie(authTokenName)
        return !hasValidAccessToken(authCookie.value)
      },
      // Returning `true` from `didAuthError` will result in `refreshAuth` below being called by the exchange.
      didAuthError: (errorContext, _operation) => isAuthenticationError(errorContext),
      // When `refreshAuth` is called for an operation URQL's auth exchange automatically flags the operation as having its authentication refresh attempted.
      // After this function runs the auth exchange adds the operation back into the request queue to retry to request.
      // The auth exchange will only call `refreshAuth` if the operations has NOT already be flagged for attempting an authentication refresh.
      // This means that each operation that fails due to authentication will only automatically retry once instead of creating an infinite loop.
      // There is also a retry exchange that is maintained by URQL. However, that exchange is for retrying requests NOT caused by failing to authenticate.
      refreshAuth: async() => await getAuthToken(authTokenName),
    }
  })
}

// TODO: The `duplicateMutationInstanceCorrector` exchange is a temporary solution to a problem where duplicate mutations with the same `_instance` value can be created when multiple mutations are made at the same time with the same operation key (the variables and operation match).
//       There is a separate concern where multiple mutations are made close together with different variables but the same operation.
//       This scenario does not have the same issue with duplicate `_instance` values, but it can still result in responses resolving in a different order than they were sent due to network latency, causing stale data to be shown in the UI until another mutation or refetch occurs.
//       Perhaps both of these scenarios could be more elegantly solved by creating an URQL exchange that automatically queues operations for the same mutation.
//       URQL's context API could also be used to specify a key in the mutation's variables to determine if they should be queued together. This way multiple mutations, but for different entities, could be made at the same time without being queued together and without risking stale data from responses resolving in a different order than they were sent.
function duplicateMutationInstanceCorrector({ forward }) {
  // Maps mutation keys to an array of pending mutations for the given key.
  const mutationInstanceMap = {}

  return (operations$) => {
    // Create a source of only non-mutation operations.
    const nonMutations$ = pipe(
      operations$,
      filter(operation => operation.kind !== 'mutation')
    )

    // Create a source of only mutation operations.
    // Creating a separate source ensures only mutations will be added to `mutationInstanceMap`.
    const mutations$ = pipe(
      operations$,
      filter(operation => operation.kind === 'mutation'),
      // For each mutation add it to the `mutationInstanceMap` based on the mutation's key.
      map((op) => {
        const opKey = op.key
        if (!mutationInstanceMap[opKey]) {
          mutationInstanceMap[opKey] = []
        }

        mutationInstanceMap[opKey].push(op)

        return op
      })
    )

    const result$ = pipe(
      // Merge the 2 sources into one source stream.
      merge([nonMutations$, mutations$]),
      // Pass the operations from the merged source to the next exchange.
      forward,
      // Use the result of each operation and correct for any duplicated `_instance` values.
      map((opResult) => {
        const opKey = opResult.operation.key
        const opInstances = mutationInstanceMap[opKey]

        // If there are no operations in `mutationInstanceMap` for the result's operation key,
        // delete the entry from `mutationInstanceMap` and return the operation result.
        if (!opInstances?.length) {
          delete mutationInstanceMap[opKey]
          return opResult
        }

        // Find the index of the operation where its `_instance` value matches the operation result's `_instance`.
        let opIndex = opInstances.findIndex(op => op.context._instance === opResult.operation.context._instance)

        // If no index is found for the current result's `_instance` then it means that Graph Cache already sent a result with the same `_instance` value.
        // This happens when 2 identical mutations become pending at the same time.
        // Graph Cache sends the result for the latest instance whenever either instance gets a result,
        // instead of sending the result for the correct instance.
        // In this scenario use the first operation in the map to ensure all duplicate mutations eventually resolve.
        if (opIndex < 0) {
          opIndex = 0
        }

        const [op] = opInstances.splice(opIndex, 1)

        // If there are no more operations for the given key in `mutationInstanceMap` then delete the key's entry.
        if (!opInstances.length) {
          delete mutationInstanceMap[opKey]
        }

        // Update the operation result to have the other operation instance's `_instance` value to ensure it will also resolve.
        opResult.operation.context._instance = op.context._instance

        return opResult
      })
    )

    return result$
  }
}

export default defineNuxtPlugin((nuxtApp) => {
  const { public: config } = useRuntimeConfig()

  const authExchange = getAuthExchange(config.authTokenName)

  const getClient = () => {
    return new Client({
      url: config.graphqlServerEndpoint,
      // The order of exchanges matters as synchronous exchanges must be placed before asynchronous exchanges.
      // See https://commerce.nearform.com/open-source/urql/docs/advanced/authoring-exchanges/#synchronous-first-asynchronous-last for details.
      exchanges: [
        duplicateMutationInstanceCorrector,
        cacheExchange({
          updates: {
            Mutation: {
              createNote(result, args, cache) {
                cache
                  .inspectFields('Query')
                  .filter((field) => {
                    // Filter for instances of the `notes` query where the `number` argument is the `policyNumber` used in the `createNote` mutation.
                    return field.fieldName === 'notes' && field.arguments.number === args.data.policyNumber
                  })
                  .forEach((field) => {
                    // Get the current cache of notes for this instance of `notes`.
                    const notes = cache.resolve('Query', field.fieldKey)

                    // Update the cache so that URQL understands which `notes` list the newly created `note` belongs to.
                    cache.link(
                      'Query',
                      field.fieldKey,
                      Array.isArray(notes) ? [result.createNote, ...notes] : [result.createNote]
                    )
                  })
              },
              issueTransaction(_result, _args, cache) {
                // Upon issuing a transaction this updater invalidates all Policy/PolicyQuote/Ledger objects in the cache.
                // This avoids any stale data being left in the cache that could have been affected by the issuance of a transaction.
                cache.invalidate('Policy')
                cache.invalidate('PolicyQuote')
                cache.invalidate('Ledger')
              },
              bindAutoQuote(_result, _args, cache) {
                // Issuing an AutoQuote can affect related AutoPolicy, AutoQuote, and Account
                // data, so invalidate every cached object of those types here.
                // This prevents stale entries from lingering in the cache after an issuance.
                cache.invalidate('AutoPolicy')
                cache.invalidate('AutoQuote')
                cache.invalidate('Account')
              },
              generateQuoteDocument(_result, _args, cache) {
                // Quote document generation updates document history on AutoQuote.
                // Invalidate related entities so active quote views requery with fresh documents.
                cache.invalidate('AutoQuote')
              },
              generatePolicyDocument(_result, _args, cache) {
                // Policy document generation can affect policy/quote document lists.
                // Invalidate related entities so active views refresh automatically.
                cache.invalidate('Policy')
                cache.invalidate('AutoPolicy')
                cache.invalidate('AutoQuote')
              },
              // Regarding the following create transaction updaters:
              // The `Policy` GraphQL type contains an array of issued transactions related to the policy.
              // However, since transactions are only added to this array in GraphQL when they are issued,
              // creating a new transaction should not update a cached `Policy` object.
              // URQL can not automatically know about this behavior.
              // These updaters explicitly do nothing to inform URQL's GraphCache system,
              // and to avoid any unintended cache updates or refetches as a result of a transaction's creation.
              // The `issueTransaction` updater will correctly evict the cache when a transaction is issued.
              createTransaction: () => null,
              createRescheduleTransaction: () => null,
              createCancellationTransaction: () => null,
              createReinstatementTransaction: () => null,
              // End of create transaction updaters.
            },
          },
          keys: {
            // The `ID` field in a GraphQL type is used by Graph Cache to uniquely identify an entity.
            // Some types in our GraphQL schema do not have an `ID` field as the type has no UUID to uniquely identify the entity.
            // This prevents the caching system from caching these types on their own.
            // Instead the entity is automatically cached as a part of its parent object (ex: the `Coverage` object will be cached in its parent `Transaction`'s cache).
            // This scenario results in a warning that can be silenced by configuring the type to explicitly return `null` for its cache key.
            // Documentation: https://nearform.com/open-source/urql/docs/graphcache/normalized-caching/#custom-keys-and-non-keyable-entities
            // ----- GQL Types Cached By A Parent Object ----- //
            UserIntegrations: () => null,
            UnbindableReason: () => null,
            Coverage: () => null,
            Location: () => null,
            Property: () => null,
            MineSubsidenceMulti: () => null,
            Earthquake: () => null,
            BlanketLimits: () => null,
            Applicant: () => null,
            MailingAddress: () => null,
            // ----- GQL Types Where The ID Field Is Missing In At Least One Query ----- //
            // ----- GQL Types That Need Configured To Be Cached By A Parent Object (types with no id field) ----- //
            // Document (graphql/queries/policyQuoteOverview.gql)
            // Inspection (graphql/queries/policyQuoteOverview.gql)
            // Underwriting (graphql/queries/policyQuoteOverview.gql)
            // CancellationInfo (graphql/queries/policyOverview.gql)
            // LineageData (graphql/queries/policyOverview.gql)
            // PrimaryContact (graphql/queries/policyOverview.gql)
            // HumanNamedInsured (graphql/queries/policyOverview.gql)
            // Mortgagee (graphql/queries/policyOverview.gql)
          },
        }),
        mapExchange({
          onError(errorContext, operation) {
            if (isAuthenticationError(errorContext)) {
              nuxtApp.$store.dispatch('self/logout')
            }

            // DEPRECATED (flagged for removal in a follow-up ticket): `skipGlobalErrorHandling`
            // is an option that can be provided to clients and individual URQL requests.
            // If true then skip the global error handler behavior entirely, which also suppresses
            // Bugsnag logging. Kept to preserve existing callsites; prefer `disableErrorModal` below.
            if (operation.context.skipGlobalErrorHandling) {
              return
            }

            // DEPRECATED (flagged for removal in a follow-up ticket): an array
            // `skipGlobalErrorForHttpStatusCodes` can be provided. If provided and a status code
            // matches, we skip the global error handler entirely, which also suppresses Bugsnag
            // logging. Kept to preserve existing callsites; prefer `disableErrorModal` below.
            const matches = errorContext.graphQLErrors.some((err) => {
              return operation.context.skipGlobalErrorForHttpStatusCodes?.includes(err.extensions.response.status)
            })
            if (matches) {
              return
            }

            // `disableErrorModal` disables only the default error modal while preserving the
            // global error handler's Bugsnag logging. It can be a boolean, or a function
            // `(errorContext) => boolean` that receives URQL's error context and returns whether
            // to suppress the modal for this error.
            const disableModalOption = operation.context.disableErrorModal
            const disableErrorModal = typeof disableModalOption === 'function'
              ? Boolean(disableModalOption(errorContext))
              : Boolean(disableModalOption)

            // #APOLLO-CLEANUP: Once Apollo Client is removed from Portal Next these error handling functions should be refactored to avoid this unclear mapping.
            ErrorHandler(errorContext, nuxtApp, { disableErrorModal })
          },
        }),
        authExchange,
        fetchExchange,
      ],
    })
  }

  const client = ref(getClient())

  const isAuthenticated = () => {
    const authCookie = useCookie(config.authTokenName)

    return hasValidAccessToken(authCookie.value)
  }

  const login = (token) => {
    if (!client.value) {
      client.value = getClient()
    }

    // Update the auth token cookie.
    const authCookie = useCookie(config.authTokenName)

    authCookie.value = token
  }

  const logout = () => {
    const authCookie = useCookie(config.authTokenName)
    authCookie.value = null
  }

  nuxtApp.vueApp.use(urql, client)

  const provided = {
    isAuthenticated,
    login,
    logout,
  }

  // By making the `client` property have a getter we are able to ensure that the latest client is utilized when `$urql.client` is referenced in the application.
  Object.defineProperty(provided, 'client', {
    get: function() {
      return client.value
    },
  })

  // This provides the URQL Client as `$urql` on the Nuxt App context.
  // The functions on the `Client` type are different from the component cognizant Vue Composable functions such as `useQuery`.
  // This is because these Client functions could be called from anywhere as opposed exclusively from the setup of a component.
  // More information regarding how to use these functions can be found in the URQL documentation here: https://nearform.com/open-source/urql/docs/api/core/#client
  return {
    provide: {
      urql: provided,
    },
  }
})
